import { useCallback } from 'react'
import PropTypes from 'prop-types'
import { Router, Route } from 'react-router-dom'
import { QueryParamProvider } from 'use-query-params'
import { QueryClientProvider } from 'react-query'
import { ReactQueryDevtools } from 'react-query/devtools'
import DateAdapter from '@mui/lab/AdapterDateFns'
import esLocale from 'date-fns/locale/es'

import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider as LegacyThemeProvider } from '@mui/styles'
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import { IconContext } from 'phosphor-react'

import AppAlertsProvider from '../App/AppAlerts/AppAlertsProvider'
import ErrorCrashBoundary from '../context/errors/ErrorCrashBoundary'

import LocaleProvider from '../i18n/LocaleProvider'
import AuthProvider from '../auth/AuthProvider'
import AuthWall from '../auth/AuthWall'
import UserProvider from '../auth/UserProvider'
import theme from '../ui/theme'

import PrinterProvider from './PrinterProvider'

const ICON_VALUE = {
  color: 'currentColor',
  weight: 'light',
  width: 32,
  mirrored: false,
}

const AppProviders = ({ history, queryClient, children }) => {
  const clearQueryCache = useCallback(() => {
    queryClient.clear()
  }, [queryClient])

  return (
    <LocaleProvider>
      <IconContext.Provider value={ICON_VALUE}>
        <StyledEngineProvider injectFirst>
          {/** IMPORTANT This legacy provider is needed, even though, as per the Material docs, it shouldn't be the case:
           * https://mui.com/material-ui/migration/v5-style-changes/#update-themeprovider-import
           * We needed to bump MUI version to @mui/material@~5.0.4, and this derived in some MUI sub-dependencies (e.g.
           * @mui/system) to bump to 5.11.x instead of being hold at 5.0.x because of their way to specify dependencies.
           * This mismatch breaks the legacy theming support (based on JSS) with the new ThemeProvider; thus the legacy
           * provider. This provider should be removed after upgrading all `makeStyles` to the new way of theming.
           */}
          <LegacyThemeProvider theme={theme}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <LocalizationProvider dateAdapter={DateAdapter} locale={esLocale}>
                <QueryClientProvider client={queryClient}>
                  <ErrorCrashBoundary>
                    <Router history={history}>
                      <QueryParamProvider ReactRouterRoute={Route}>
                        <AuthProvider logoutCallback={clearQueryCache}>
                          {/** TODO This wall is not a provider, so it shouldn't be here. However, some children
                           * providers (at least, the UserProvider) rely on this behaviour or they crash. These should
                           * be fixed first. */}
                          <AuthWall>
                            <UserProvider>
                              <PrinterProvider>
                                <AppAlertsProvider>
                                  {children}
                                </AppAlertsProvider>
                              </PrinterProvider>
                            </UserProvider>
                          </AuthWall>
                        </AuthProvider>
                      </QueryParamProvider>
                    </Router>
                  </ErrorCrashBoundary>
                  <ReactQueryDevtools initialIsOpen={false} />
                </QueryClientProvider>
              </LocalizationProvider>
            </ThemeProvider>
          </LegacyThemeProvider>
        </StyledEngineProvider>
      </IconContext.Provider>
    </LocaleProvider>
  )
}

AppProviders.propTypes = {
  history: PropTypes.object,
  queryClient: PropTypes.object,
  children: PropTypes.node,
}

export default AppProviders
